// @section 1 Acquisitions / CIM Explorer — port of CIM-EXPLORER-2026-06-02.html.
// Sortable table of every listing with a structured CIM extract + side panel
// with multi-year history, service/client mix, ops/staff/tech detail.
//
// Tier-1 logic (carried verbatim from the HTML footer):
//   rev $300K–$5M  AND  delivery in {virtual, hybrid}  AND
//   margin > 25%   AND  audit pct < 50%
//
// Source: each deal's cim_extracts array (multi-year). Latest = the row with
// is_year_summary=true if present, else the largest cim_year.

import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { type CimExtract, type Deal } from '../../lib/acquisitions';
import { useAcquisitions } from '../../lib/AcquisitionsContext';
import { useIsMobile } from '../../lib/useIsMobile';

type Row = {
  deal: Deal;
  latest: CimExtract;
  history: CimExtract[];
  marginPct: number | null;
  auditPct: number | null;
  tier1: boolean;
  tier1Reasons: { sizeOk: boolean; deliveryOk: boolean; marginOk: boolean; auditOk: boolean };
  clientCount: number | null;
};

type SortKey =
  | 'code' | 'business_name' | 'broker' | 'state'
  | 'asking_price' | 'revenue' | 'mult_rev' | 'cash_flow' | 'mult_sde'
  | 'margin_pct' | 'delivery' | 'client_count';

const ROWS_SQL_NOTES = (
  <>
    Source: <code>acquisitions.cim_extracts</code> on <code>sandbox_poc</code> · {' '}
    Tier-1: rev $300K–$5M + virtual/hybrid + margin &gt; 25% + audit &lt; 50%.
  </>
);

export default function CimExplorer() {
  const acquisitions = useAcquisitions();
  const [delivery, setDelivery] = useState<'all' | 'virtual' | 'hybrid' | 'in_office'>('all');
  const [tier1, setTier1] = useState<'all' | 'yes'>('all');
  const [conf, setConf] = useState<'all' | 'high' | 'medium' | 'low' | 'failed'>('all');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'mult_sde', dir: 'asc' });
  const [selected, setSelected] = useState<string | null>(null);
  // @block 1.1 Phone: tighten gutters. The 14-col table stays as-is and scrolls
  // horizontally inside its overflow:auto wrapper (the cim-panel goes full-width).
  const isMobile = useIsMobile();
  const px = isMobile ? 14 : 26;

  const allRows = useMemo<Row[]>(() => buildRows(acquisitions.deals), [acquisitions.deals]);

  const rows = useMemo(() => {
    return allRows
      .filter((r) => delivery === 'all' || r.latest.delivery_model === delivery)
      .filter((r) => tier1 === 'all' || r.tier1)
      .filter((r) => conf === 'all' || r.latest.extraction_confidence === conf)
      .sort((a, b) => compare(a, b, sort.key, sort.dir));
  }, [allRows, delivery, tier1, conf, sort]);

  const selectedRow = selected ? allRows.find((r) => r.deal.deal_id === selected) ?? null : null;

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', minHeight: 0 }}>
      <div style={{
        padding: `10px ${px}px`,
        borderBottom: '1px solid var(--t2-border)',
        display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', fontSize: 12,
      }}>
        <span style={{ color: 'var(--tx-3)' }}>delivery:</span>
        <Chip active={delivery === 'all'}        onClick={() => setDelivery('all')}>All</Chip>
        <Chip active={delivery === 'virtual'}    onClick={() => setDelivery('virtual')} bg="rgba(96,165,250,0.18)" color="#60a5fa">virtual</Chip>
        <Chip active={delivery === 'hybrid'}     onClick={() => setDelivery('hybrid')} bg="rgba(167,139,250,0.18)" color="#a78bfa">hybrid</Chip>
        <Chip active={delivery === 'in_office'}  onClick={() => setDelivery('in_office')} bg="rgba(120,120,135,0.18)" color="#9aa0a8">in-office</Chip>
        <span style={{ color: 'var(--tx-3)', marginLeft: 14 }}>tier-1:</span>
        <Chip active={tier1 === 'all'} onClick={() => setTier1('all')}>All</Chip>
        <Chip active={tier1 === 'yes'} onClick={() => setTier1('yes')} bg="rgba(20,184,166,0.18)" color="#5eead4">Tier-1 only</Chip>
        <span style={{ color: 'var(--tx-3)', marginLeft: 14 }}>confidence:</span>
        <Chip active={conf === 'all'}    onClick={() => setConf('all')}>All</Chip>
        <Chip active={conf === 'high'}   onClick={() => setConf('high')} bg="rgba(52,211,153,0.18)" color="#34d399">high</Chip>
        <Chip active={conf === 'medium'} onClick={() => setConf('medium')} bg="rgba(251,191,36,0.18)" color="#fbbf24">medium</Chip>
        <Chip active={conf === 'low'}    onClick={() => setConf('low')} bg="rgba(251,113,133,0.18)" color="#fb7185">low</Chip>
        <span style={{ marginLeft: 'auto', color: 'var(--tx-4)', fontSize: 10 }}>{rows.length} / {allRows.length}</span>
      </div>

      <div style={{ overflow: 'auto', minHeight: 0 }}>
        <table className="cim-table">
          <thead>
            <tr>
              <Th k="code"          sort={sort} set={setSort}>Code</Th>
              <Th k="business_name" sort={sort} set={setSort}>Business</Th>
              <Th k="broker"        sort={sort} set={setSort}>Broker</Th>
              <Th k="state"         sort={sort} set={setSort}>ST</Th>
              <Th k="asking_price"  sort={sort} set={setSort} right>Ask</Th>
              <Th k="revenue"       sort={sort} set={setSort} right>Rev</Th>
              <Th k="mult_rev"      sort={sort} set={setSort} right>× Rev</Th>
              <Th k="cash_flow"     sort={sort} set={setSort} right>SDE</Th>
              <Th k="mult_sde"      sort={sort} set={setSort} right>× SDE</Th>
              <Th k="margin_pct"    sort={sort} set={setSort} right>Margin</Th>
              <Th k="delivery"      sort={sort} set={setSort}>Delivery</Th>
              <Th k="client_count"  sort={sort} set={setSort} right>Clients</Th>
              <th>Tier-1</th>
              <th>Conf</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.deal.deal_id}
                className={`cim-row${selected === r.deal.deal_id ? ' selected' : ''}`}
                onClick={() => setSelected(r.deal.deal_id)}
              >
                <td className="mono">{r.deal.listing.source_listing_id ?? '—'}</td>
                <td>{r.deal.listing.business_name}</td>
                <td className="muted">{r.deal.broker?.company_name ?? '—'}</td>
                <td className="mono">{r.deal.listing.location_state ?? '—'}</td>
                <td className="num">{fmtMoney(r.latest.asking_price ?? r.deal.listing.asking_price)}</td>
                <td className="num">{fmtMoney(r.latest.gross_revenue)}</td>
                <td className="num">{fmtMult(r.latest.asking_multiple_rev)}</td>
                <td className="num">{fmtMoney(r.latest.cash_flow)}</td>
                <td className="num">{fmtMult(r.latest.asking_multiple_sde)}</td>
                <td className="num">{fmtPct(r.marginPct)}</td>
                <td>
                  <DeliveryBadge model={r.latest.delivery_model} />
                </td>
                <td className="num">{r.clientCount ?? '—'}</td>
                <td>{r.tier1 ? <span className="tier1-badge">TIER-1</span> : <span style={{ color: 'var(--tx-5)', fontSize: 10 }}>—</span>}</td>
                <td><span className={`conf conf-${r.latest.extraction_confidence}`}>{r.latest.extraction_confidence}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: `10px ${px}px`, fontSize: 10, color: 'var(--tx-4)' }}>
          {ROWS_SQL_NOTES}
        </div>
      </div>

      {/* Side panel */}
      <div className={`cim-backdrop${selectedRow ? ' open' : ''}`} onClick={() => setSelected(null)} />
      <aside className={`cim-panel${selectedRow ? ' open' : ''}`}>
        {selectedRow && <SidePanel row={selectedRow} onClose={() => setSelected(null)} />}
      </aside>
    </div>
  );
}

// @section 2 Side panel
function SidePanel({ row, onClose }: { row: Row; onClose: () => void }) {
  const d = row.deal;
  const l = d.listing;
  const e = row.latest;
  return (
    <div>
      <header style={{
        padding: '14px 18px 12px',
        borderBottom: '1px solid var(--t2-border)',
        position: 'sticky', top: 0, background: 'rgba(8,15,28,0.96)',
        backdropFilter: 'blur(10px)', zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--tx-4)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
              {l.source_listing_id ?? '—'}
            </div>
            <h2 style={{
              margin: '2px 0 4px',
              fontFamily: 'var(--t-display)',
              fontSize: 18, fontWeight: 700, letterSpacing: '-0.3px',
              color: 'var(--tx-0)', lineHeight: 1.2,
            }}>{l.business_name}</h2>
            <div style={{ fontSize: 11, color: 'var(--tx-3)' }}>{d.broker?.company_name ?? 'no broker'}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'var(--t2-bg)', border: '1px solid var(--t2-border)',
              color: 'var(--tx-2)', width: 28, height: 28, borderRadius: '50%',
              fontSize: 16, cursor: 'pointer', lineHeight: 1,
            }}
          >×</button>
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {row.tier1 && <span className="tier1-badge">TIER-1</span>}
          <DeliveryBadge model={e.delivery_model} />
          <span className={`conf conf-${e.extraction_confidence}`}>{e.extraction_confidence}</span>
          {e.reason_for_sale_tag && (
            <span style={{
              fontSize: 9, padding: '2px 8px', borderRadius: 99, fontWeight: 700,
              background: 'rgba(255,255,255,0.05)', color: 'var(--tx-2)',
              textTransform: 'uppercase', letterSpacing: 0.5,
            }}>{e.reason_for_sale_tag}</span>
          )}
        </div>
        <div style={{ marginTop: 10 }}>
          <NavLink
            to={`/acquisitions/${d.deal_id}`}
            style={{ fontSize: 11, color: 'var(--brand-3)', textDecoration: 'none' }}
          >Open deal detail →</NavLink>
        </div>
      </header>

      <section style={{ padding: '12px 18px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        <PanelStat label="Asking"   v={fmtMoney(e.asking_price ?? l.asking_price)} />
        <PanelStat label="Latest Rev" v={fmtMoney(e.gross_revenue)} />
        <PanelStat label="SDE"      v={fmtMoney(e.cash_flow)} />
        <PanelStat label="× Rev"    v={fmtMult(e.asking_multiple_rev)} />
        <PanelStat label="× SDE"    v={fmtMult(e.asking_multiple_sde)} />
        <PanelStat label="Margin"   v={fmtPct(row.marginPct)} />
      </section>

      <PanelSection title="Revenue / SDE history">
        <HistoryChart history={row.history} />
      </PanelSection>

      <PanelSection title="Service mix">
        {e.service_mix ? <MixBars data={e.service_mix} /> : <Empty>No service-mix in CIM.</Empty>}
      </PanelSection>

      <PanelSection title="Client mix">
        {e.client_mix ? <MixBars data={e.client_mix} /> : <Empty>No client-mix in CIM.</Empty>}
      </PanelSection>

      <PanelSection title="Client concentration">
        {e.client_concentration ? <JsonBlock data={e.client_concentration} /> : <Empty>No concentration captured.</Empty>}
      </PanelSection>

      <PanelSection title="Staff">
        {e.staff_breakdown ? <JsonBlock data={e.staff_breakdown} /> : <Empty>No staff breakdown.</Empty>}
      </PanelSection>

      <PanelSection title="Tech stack">
        {e.tech_stack ? <JsonBlock data={e.tech_stack} /> : <Empty>No tech stack captured.</Empty>}
      </PanelSection>

      <PanelSection title="Tier-1 fit">
        <TierBreakdown row={row} />
      </PanelSection>

      <PanelSection title="Reason for sale / owner terms">
        <div style={{ fontSize: 12, color: 'var(--tx-1)', marginBottom: 6, whiteSpace: 'pre-wrap' }}>
          {l.reason_for_sale ?? <Empty>No reason captured.</Empty>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--tx-2)', whiteSpace: 'pre-wrap' }}>
          {l.owner_terms ?? <Empty>No owner-terms captured.</Empty>}
        </div>
      </PanelSection>

      <PanelSection title="Source document">
        {d.documents.length === 0 ? <Empty>No source doc linked.</Empty> : (
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {d.documents.map((doc) => (
              <li key={doc.doc_id} style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--tx-1)' }}>{doc.document_name ?? '(unnamed)'}</div>
                <div style={{ fontSize: 10, color: 'var(--tx-4)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>
                  {doc.storage_url ?? '—'}
                </div>
              </li>
            ))}
          </ul>
        )}
      </PanelSection>
    </div>
  );
}

// @section 3 Sub-components

function Chip({
  active, onClick, bg, color, children,
}: {
  active: boolean;
  onClick: () => void;
  bg?: string;
  color?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 11, fontWeight: 600,
        padding: '3px 9px', borderRadius: 99,
        background: active ? (bg ?? 'var(--brand-grad)') : 'transparent',
        color: active ? (color ?? '#062028') : (bg && color ? color : 'var(--tx-3)'),
        border: active ? 'none' : '1px solid var(--t2-border)',
        cursor: 'pointer',
        outline: active && bg ? `2px solid ${color}` : 'none',
        outlineOffset: 1,
      }}
    >
      {children}
    </button>
  );
}

function Th({
  k, sort, set, right, children,
}: {
  k: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  set: (s: { key: SortKey; dir: 'asc' | 'desc' }) => void;
  right?: boolean;
  children: React.ReactNode;
}) {
  const active = sort.key === k;
  const arrow = active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
  return (
    <th
      onClick={() => set({ key: k, dir: active && sort.dir === 'asc' ? 'desc' : 'asc' })}
      style={{ cursor: 'pointer', textAlign: right ? 'right' : 'left' }}
    >
      {children}{arrow}
    </th>
  );
}

function DeliveryBadge({ model }: { model: string | null }) {
  if (!model) return <span style={{ color: 'var(--tx-5)', fontSize: 10 }}>—</span>;
  const c =
    model === 'virtual'   ? { bg: 'rgba(96,165,250,0.18)',  color: '#60a5fa' } :
    model === 'hybrid'    ? { bg: 'rgba(167,139,250,0.18)', color: '#a78bfa' } :
    model === 'in_office' ? { bg: 'rgba(120,120,135,0.18)', color: '#9aa0a8' } :
                            { bg: 'rgba(255,255,255,0.05)', color: 'var(--tx-3)' };
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      background: c.bg, color: c.color,
      textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{model.replace('_', '-')}</span>
  );
}

function PanelStat({ label, v }: { label: string; v: string }) {
  return (
    <div style={{ background: 'rgba(10,19,34,0.5)', padding: '8px 10px', borderRadius: 6, textAlign: 'center' }}>
      <div style={{ fontSize: 9, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>{label}</div>
      <div style={{ fontFamily: 'var(--t-display)', fontSize: 17, fontWeight: 700, color: 'var(--tx-0)', fontVariantNumeric: 'tabular-nums' }}>{v}</div>
    </div>
  );
}

function PanelSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ padding: '12px 18px', borderTop: '1px solid var(--t2-border)' }}>
      <h3 style={{
        margin: '0 0 8px', fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
        color: 'var(--brand-4)', textTransform: 'uppercase',
      }}>{title}</h3>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, color: 'var(--tx-4)', fontStyle: 'italic' }}>{children}</div>;
}

function MixBars({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data)
    .filter(([k, v]) => typeof v === 'number' && k.endsWith('_pct') && k !== 'total_pct')
    .map(([k, v]) => [k.replace(/_pct$/, '').replace(/_/g, ' '), v as number] as const);
  if (entries.length === 0) return <Empty>No numeric % fields.</Empty>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {entries.map(([k, v]) => (
        <div key={k}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
            <span style={{ color: 'var(--tx-2)' }}>{k}</span>
            <span style={{ color: 'var(--tx-1)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{(v * 100).toFixed(1)}%</span>
          </div>
          <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 4, marginTop: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, v * 100)}%`, background: 'var(--brand-grad)', borderRadius: 4 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function JsonBlock({ data }: { data: Record<string, unknown> }) {
  return (
    <pre style={{
      margin: 0,
      fontSize: 10,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      color: 'var(--tx-2)',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid var(--t2-border)',
      borderRadius: 6, padding: 8,
      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      lineHeight: 1.5,
    }}>{JSON.stringify(data, null, 2)}</pre>
  );
}

function HistoryChart({ history }: { history: CimExtract[] }) {
  // history is sorted by [is_year_summary, cim_year DESC] — show oldest → newest with
  // summary on the far right if present.
  const pts = [...history]
    .filter((h) => h.cim_year || h.is_year_summary)
    .sort((a, b) => {
      if (a.is_year_summary !== b.is_year_summary) return a.is_year_summary ? 1 : -1;
      return (a.cim_year ?? 0) - (b.cim_year ?? 0);
    });
  if (pts.length === 0) return <Empty>No multi-year history.</Empty>;

  const max = Math.max(...pts.map(p => Math.max(p.gross_revenue ?? 0, p.cash_flow ?? 0)));
  if (max === 0) return <Empty>No revenue / SDE values.</Empty>;

  const W = 320, H = 110, padL = 32, padR = 8, padT = 8, padB = 22;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const x = (i: number) => padL + (pts.length === 1 ? innerW / 2 : (innerW * i) / (pts.length - 1));
  const y = (v: number) => padT + innerH - (innerH * v) / max;

  const revPath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)},${y(p.gross_revenue ?? 0)}`).join(' ');
  const sdePath = pts
    .map((p, i) => (p.cash_flow == null ? null : `${i === 0 ? 'M' : 'L'} ${x(i)},${y(p.cash_flow)}`))
    .filter(Boolean).join(' ');

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
        {/* gridlines */}
        <line x1={padL} y1={padT + innerH} x2={W - padR} y2={padT + innerH} stroke="var(--t2-border)" />
        <line x1={padL} y1={padT}          x2={W - padR} y2={padT}          stroke="var(--t2-border)" strokeDasharray="2,3" />
        {/* rev line */}
        <path d={revPath} fill="none" stroke="var(--brand-3)" strokeWidth="2" />
        {pts.map((p, i) => p.gross_revenue != null && (
          <circle key={'r' + i} cx={x(i)} cy={y(p.gross_revenue)} r={3} fill="var(--brand-3)" />
        ))}
        {/* SDE line */}
        {sdePath && <path d={sdePath} fill="none" stroke="var(--pos)" strokeWidth="2" strokeDasharray="4,3" />}
        {pts.map((p, i) => p.cash_flow != null && (
          <circle key={'s' + i} cx={x(i)} cy={y(p.cash_flow)} r={3} fill="var(--pos)" />
        ))}
        {/* x labels */}
        {pts.map((p, i) => (
          <text key={'l' + i} x={x(i)} y={H - 6}
            fontSize="10" fill="var(--tx-4)" textAnchor="middle"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {p.is_year_summary ? 'TTM' : p.cim_year}
          </text>
        ))}
        {/* y max label */}
        <text x={4} y={padT + 8} fontSize="9" fill="var(--tx-4)">{fmtMoney(max)}</text>
      </svg>
      <div style={{ display: 'flex', gap: 12, fontSize: 10, marginTop: 4 }}>
        <span style={{ color: 'var(--brand-3)' }}>● Revenue</span>
        <span style={{ color: 'var(--pos)' }}>● SDE</span>
      </div>
    </div>
  );
}

function TierBreakdown({ row }: { row: Row }) {
  const { tier1Reasons: r, latest } = row;
  const pct = (v: number | null) => v == null ? '—' : `${(v * 100).toFixed(1)}%`;
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 11 }}>
      <Crit ok={r.sizeOk}     label={`Revenue $300K–$5M`}    actual={fmtMoney(latest.gross_revenue)} />
      <Crit ok={r.deliveryOk} label={`Delivery ∈ {virtual, hybrid}`} actual={latest.delivery_model ?? '—'} />
      <Crit ok={r.marginOk}   label={`Margin > 25%`}                actual={pct(row.marginPct)} />
      <Crit ok={r.auditOk}    label={`Audit < 50%`}                 actual={pct(row.auditPct)} />
    </ul>
  );
}

function Crit({ ok, label, actual }: { ok: boolean; label: string; actual: string }) {
  return (
    <li style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
    }}>
      <span style={{ color: ok ? 'var(--pos)' : 'var(--neg)', marginRight: 6 }}>{ok ? '✓' : '✗'}</span>
      <span style={{ flex: 1, color: 'var(--tx-1)' }}>{label}</span>
      <span style={{ color: 'var(--tx-3)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{actual}</span>
    </li>
  );
}

// @section 4 Pure helpers

function buildRows(deals: Deal[]): Row[] {
  const rows: Row[] = [];
  for (const d of deals) {
    const history = d.cim_extracts;
    if (history.length === 0) continue;
    // Latest: prefer is_year_summary, else largest year.
    const summary = history.find((h) => h.is_year_summary);
    const latest = summary ?? [...history].sort((a, b) => (b.cim_year ?? 0) - (a.cim_year ?? 0))[0];

    const marginPct = latest.gross_margin_pct
      ?? (latest.cash_flow != null && latest.gross_revenue ? latest.cash_flow / latest.gross_revenue : null);
    const auditPct = readPct(latest.service_mix, 'audit_pct') ?? readPct(latest.service_mix, 'audit_review_pct');

    const sizeOk = latest.gross_revenue != null && latest.gross_revenue >= 300_000 && latest.gross_revenue <= 5_000_000;
    const deliveryOk = latest.delivery_model === 'virtual' || latest.delivery_model === 'hybrid';
    const marginOk = marginPct != null && marginPct > 0.25;
    const auditOk = auditPct == null || auditPct < 0.50;
    const tier1 = sizeOk && deliveryOk && marginOk && auditOk;

    const clientCount =
      (latest.client_concentration as any)?.client_count
      ?? (d.listing.client_count ?? null);

    rows.push({
      deal: d, latest, history,
      marginPct, auditPct,
      tier1,
      tier1Reasons: { sizeOk, deliveryOk, marginOk, auditOk },
      clientCount: typeof clientCount === 'number' ? clientCount : null,
    });
  }
  return rows;
}

function readPct(obj: Record<string, unknown> | null, key: string): number | null {
  if (!obj) return null;
  const v = obj[key];
  return typeof v === 'number' ? v : null;
}

function compare(a: Row, b: Row, key: SortKey, dir: 'asc' | 'desc'): number {
  const sign = dir === 'asc' ? 1 : -1;
  const get = (r: Row): string | number => {
    switch (key) {
      case 'code':          return r.deal.listing.source_listing_id ?? '';
      case 'business_name': return r.deal.listing.business_name ?? '';
      case 'broker':        return r.deal.broker?.company_name ?? '';
      case 'state':         return r.deal.listing.location_state ?? '';
      case 'asking_price':  return r.latest.asking_price ?? r.deal.listing.asking_price ?? 0;
      case 'revenue':       return r.latest.gross_revenue ?? 0;
      case 'mult_rev':      return r.latest.asking_multiple_rev ?? 0;
      case 'cash_flow':     return r.latest.cash_flow ?? 0;
      case 'mult_sde':      return r.latest.asking_multiple_sde ?? 99;
      case 'margin_pct':    return r.marginPct ?? 0;
      case 'delivery':      return r.latest.delivery_model ?? '';
      case 'client_count':  return r.clientCount ?? 0;
    }
  };
  const va = get(a); const vb = get(b);
  if (typeof va === 'number' && typeof vb === 'number') return sign * (va - vb);
  return sign * String(va).localeCompare(String(vb));
}

function fmtMoney(v: number | null | undefined): string {
  if (v == null) return '—';
  return '$' + Math.round(Number(v)).toLocaleString();
}
function fmtPct(v: number | null | undefined): string {
  if (v == null) return '—';
  return (Number(v) * 100).toFixed(1) + '%';
}
function fmtMult(v: number | null | undefined): string {
  if (v == null) return '—';
  return Number(v).toFixed(2) + '×';
}
